@if (editable) {
  <div
    class="space-y-6"
  >
    <!-- Income Statement Information -->
    <div
      class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
    >
      <div class="mb-5">
        <h3
          class="text-lg font-semibold text-[#1E2A78]"
        >
          {{ 'INCOME_STATEMENT.INFO_TITLE' | translate }}
        </h3>

        <p
          class="mt-1 text-sm text-gray-500"
        >
          {{ 'INCOME_STATEMENT.INFO_SUBTITLE' | translate }}
        </p>
      </div>

      <!-- Institution Name -->
      <div class="mb-5">
        <label
          class="mb-2 block text-sm font-medium text-gray-700"
        >
          {{ 'INCOME_STATEMENT.INSTITUTION_NAME' | translate }}
        </label>

        <input
          type="text"
          [value]="
            demographicModel.institutionName ||
            institutionName ||
            ''
          "
          readonly
          [placeholder]="'INCOME_STATEMENT.INSTITUTION_NAME_PLACEHOLDER' | translate"
          class="w-full cursor-not-allowed rounded-xl border border-gray-300 bg-gray-100 px-4 py-3 text-gray-700 outline-none"
        />
      </div>

      <app-dynamic-form
        [fields]="demographicFields"
        [model]="demographicModel"
        (formChange)="onFormChange($event)"
        (formSubmit)="onSubmit($event)"
      >
      </app-dynamic-form>

      @if (financialProfile) {
        <p class="mt-4 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-[#1E2A78]">
          {{ 'FINANCIAL_PROFILE.FROM_FINANCIAL' | translate }}
        </p>
      } @else if (financialProfileLoaded) {
        <p class="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {{ 'FINANCIAL_PROFILE.FINANCIAL_NOT_SAVED' | translate }}
        </p>
      }

      @if (errorMessage) {
        <div
          class="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {{ errorMessage }}
        </div>
      }

      @if (successMessage) {
        <div
          class="mt-5 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700"
        >
          {{ successMessage }}
        </div>
      }

      @if (saving) {
        <div
          class="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700"
        >
          {{ 'INCOME_STATEMENT.SAVING' | translate }}
        </div>
      }
    </div>

    <!-- Income Statement Data -->
    <div
      class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
    >
      <!-- Header -->
      <div
        class="flex flex-col gap-4 border-b border-gray-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h3
            class="text-lg font-semibold text-[#1E2A78]"
          >
            {{ 'INCOME_STATEMENT.DATA_TITLE' | translate }}
          </h3>

          <p
            class="mt-1 text-sm text-gray-500"
          >
            {{ 'INCOME_STATEMENT.DATA_SUBTITLE' | translate }}
          </p>
        </div>

        <div
          class="flex items-center gap-2"
        >
          @if (!financialProfile) {
            <select
              [(ngModel)]="selectedYear"
              class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            >
              <option value="">
                {{ 'INCOME_STATEMENT.SELECT_YEAR' | translate }}
              </option>

              @for (year of availableYears; track year) {
                <option
                  [value]="year"
                  [disabled]="
                    years.includes(year)
                  "
                >
                  {{ year }}
                </option>
              }
            </select>

            <button
              type="button"
              (click)="addYear()"
              class="rounded-lg bg-[#1E2A78] px-4 py-2 text-sm text-white transition hover:bg-[#16205e]"
            >
              {{ 'INCOME_STATEMENT.ADD_YEAR' | translate }}
            </button>
          }

          <app-excel-upload
            [sheetName]="'Income Statement'"
            [startMarker]="'Income Statement'"
            [endMarkers]="['Profitability', 'Liquidity Management', 'Balance Sheet', 'Ratios']"
            [sections]="financialSections"
            (parsed)="onExcelParsed($event)"
          ></app-excel-upload>
        </div>
      </div>

      <!-- Import Message -->
      @if (fileUploadError) {
        <div
          class="mx-6 mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-600"
        >
          {{ fileUploadError }}
        </div>
      }

      <!-- Loading -->
      @if (loadingSavedData) {
        <div
          class="p-6 text-center text-sm text-gray-500"
        >
          {{ 'INCOME_STATEMENT.LOADING_SAVED' | translate }}
        </div>
      }

      <!-- Empty State -->
      @if (!loadingSavedData && financialSections.length === 0) {
        <div
          class="p-10 text-center text-gray-500"
        >
          {{ 'INCOME_STATEMENT.NO_CATEGORIES' | translate }}
        </div>
      }

      <!-- Sections -->
      @if (!loadingSavedData) {
        <div
          class="space-y-4 p-5"
        >
          @for (section of financialSections; track section.statementTypeSequence) {
            <div
              class="overflow-hidden rounded-xl border border-gray-200"
            >
              <!-- Section Header -->
              <div
                class="flex cursor-pointer items-center justify-between gap-4 bg-indigo-50 px-4 py-3 transition hover:bg-indigo-100"
                (click)="toggle(section)"
              >
                <div
                  class="flex min-w-0 items-center gap-3"
                >
                  <input
                    type="checkbox"
                    class="h-4 w-4 shrink-0 accent-[#1E2A78]"
                    [ngModel]="section.checked"
                    (ngModelChange)="
                      section.checked = $event;
                      toggleSectionRows(section)
                    "
                    (click)="
                      $event.stopPropagation()
                    "
                  />

                  <div class="min-w-0">
                    <span
                      class="font-semibold text-indigo-800"
                    >
                      {{ section.title | translate }}
                    </span>

                    @if (section.description) {
                      <p
                        class="mt-0.5 truncate text-xs text-indigo-600"
                      >
                        {{ section.description | translate }}
                      </p>
                    }
                  </div>
                </div>

                <span
                  class="shrink-0 text-xl text-gray-500"
                >
                  {{
                    section.expanded
                      ? '−'
                      : '+'
                  }}
                </span>
              </div>

              <!-- Section Body -->
              @if (section.expanded) {
                <div
                  class="bg-white"
                >
                  <div class="overflow-x-auto">
                    <div
                      [style.min-width.px]="
                        300 +
                        years.length * 170
                      "
                    >
                      <!-- Years Header -->
                      <div
                        class="grid gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-600"
                        [style.grid-template-columns]="
                          'minmax(260px, 1.2fr) repeat(' +
                          years.length +
                          ', minmax(150px, 1fr))'
                        "
                      >
                        <div>
                          {{ 'INCOME_STATEMENT.ITEM' | translate }}
                        </div>

                        @for (year of years; track year) {
                          <div
                            class="flex items-center justify-end gap-2"
                          >
                            <span>
                              {{ year }}
                            </span>

                            @if (financialProfile) {
                              <span class="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-[#1E2A78]">
                                {{ getFinancialStatementTypeLabel(statementTypeForYear(year)) | translate }}
                              </span>
                            } @else {
                              <button
                                type="button"
                                (click)="
                                  removeYear(year)
                                "
                                class="text-xs font-bold text-red-500 hover:text-red-700"
                                [title]="'INCOME_STATEMENT.REMOVE_YEAR' | translate"
                              >
                                ✕
                              </button>
                            }
                          </div>
                        }
                      </div>

                      <!-- Value Rows -->
                      @for (row of section.rows; track row.valueSequence) {
                        <div
                          class="grid items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-gray-50"
                          [style.grid-template-columns]="
                            'minmax(260px, 1.2fr) repeat(' +
                            years.length +
                            ', minmax(150px, 1fr))'
                          "
                        >
                          <!-- Row Label -->
                          <div
                            class="flex items-center gap-3 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              class="h-4 w-4 shrink-0 accent-[#1E2A78]"
                              [ngModel]="row.checked"
                              (ngModelChange)="
                                row.checked = $event;
                                onRowCheckedChange(
                                  section
                                )
                              "
                            />

                            <span>
                              {{ row.label | translate }}
                            </span>
                          </div>

                          <!-- Inputs -->
                          @for (year of years; track year) {
                            <div>
                              <input
                                type="text"
                                appAmountInput
                                [amountAllowNegative]="true"
                                [(ngModel)]="
                                  demographicModel[
                                    getFieldKey(
                                      row.key,
                                      year
                                    )
                                  ]
                                "
                                [disabled]="
                                  !row.checked
                                "
                                [placeholder]="'INCOME_STATEMENT.ENTER_VALUE' | translate"
                                class="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-end text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                              />
                            </div>
                          }
                        </div>
                      }

                      <!-- Total row (computed in code, not from Excel) -->
                      @if (section.rows.length) {
                        <div
                          class="grid items-center gap-3 border-t-2 border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-[#1E2A78]"
                          [style.grid-template-columns]="
                            'minmax(260px, 1.2fr) repeat(' +
                            years.length +
                            ', minmax(150px, 1fr))'
                          "
                        >
                          <div class="flex items-center font-bold">
                            {{ getSectionTotalLabel(section) | translate }}
                          </div>

                          @for (year of years; track year) {
                            <div
                              class="pe-3 text-end"
                            >
                              {{ getSectionTotalValue(section, year) | number : '1.0-0' }}
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </div>

                  <!-- No Rows -->
                  @if (section.rows.length === 0) {
                    <div
                      class="p-6 text-center text-sm text-gray-500"
                    >
                      {{ 'INCOME_STATEMENT.NO_ITEMS' | translate }}
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  </div>
} @else {
  <!-- Preview Mode -->
  <div class="space-y-6">
    <!-- Information Preview -->
    <div
      class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
    >
      <h3
        class="mb-5 text-lg font-semibold text-[#1E2A78]"
      >
        {{ 'INCOME_STATEMENT.INFO_TITLE' | translate }}
      </h3>

      <div
        class="grid grid-cols-1 gap-5 text-sm md:grid-cols-2 lg:grid-cols-5"
      >
        <!-- Institution Name -->
        <div>
          <p
            class="mb-1 text-gray-500"
          >
            {{ 'INCOME_STATEMENT.INSTITUTION_NAME' | translate }}
          </p>

          <p
            class="font-medium text-gray-800"
          >
            {{
              demographicModel
                .institutionName ||
              institutionName ||
              '-'
            }}
          </p>
        </div>

        <!-- Statement Type -->
        <div>
          <p
            class="mb-1 text-gray-500"
          >
            {{ 'INCOME_STATEMENT.FINANCIAL_STATEMENT_TYPE' | translate }}
          </p>

          <p
            class="font-medium text-gray-800"
          >
            @if (financialProfile) {
              @for (year of years; track year) {
                <span class="block">{{ year }}: {{ getFinancialStatementTypeLabel(statementTypeForYear(year)) | translate }}</span>
              }
            } @else {
              {{ getFinancialStatementTypeLabel(demographicModel.financialStatementType) | translate }}
            }
          </p>
        </div>

        <!-- Reporting Currency -->
        <div>
          <p
            class="mb-1 text-gray-500"
          >
            {{ 'INCOME_STATEMENT.REPORTING_CURRENCY' | translate }}
          </p>

          <p
            class="font-medium text-gray-800"
          >
            {{
              getReportingCurrencyLabel(
                demographicModel
                  .reportingCurrency
              ) | translate
            }}
          </p>
        </div>

        <!-- Financial Year End -->
        <div>
          <p
            class="mb-1 text-gray-500"
          >
            {{ 'INCOME_STATEMENT.FINANCIAL_YEAR_END' | translate }}
          </p>

          <p
            class="font-medium text-gray-800"
          >
            {{
              demographicModel
                .financialYearEnd ||
              '-'
            }}
          </p>
        </div>

        <!-- Consolidated -->
        <div>
          <p
            class="mb-1 text-gray-500"
          >
            {{ 'INCOME_STATEMENT.CONSOLIDATED_FINANCIALS' | translate }}
          </p>

          <p
            class="font-medium text-gray-800"
          >
            {{ (
              demographicModel
                .isConsolidated === true ||
              demographicModel
                .isConsolidated === 'true' ||
              demographicModel
                .isConsolidated === 1
                ? 'COMMON.YES'
                : 'COMMON.NO') | translate
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Data Preview -->
    @for (section of financialSections; track section.statementTypeSequence) {
      <div
        class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
      >
        <div
          class="border-b border-indigo-100 bg-indigo-50 px-6 py-4"
        >
          <h3
            class="font-semibold text-indigo-800"
          >
            {{ section.title | translate }}
          </h3>

          @if (section.description) {
            <p
              class="mt-1 text-xs text-indigo-600"
            >
              {{ section.description | translate }}
            </p>
          }
        </div>

        <div class="overflow-x-auto">
          <table
            class="w-full min-w-[700px]"
          >
            <thead>
              <tr
                class="bg-[#1E2A78] text-white"
              >
                <th
                  class="px-5 py-3 text-start text-sm font-semibold"
                >
                  {{ 'INCOME_STATEMENT.ITEM' | translate }}
                </th>

                @for (year of years; track year) {
                  <th
                    class="px-5 py-3 text-end text-sm font-semibold"
                  >
                    {{ year }}
                  </th>
                }
              </tr>
            </thead>

            <tbody>
              @for (row of section.rows; track row.valueSequence) {
                @if (row.checked) {
                  <tr
                    class="border-b border-gray-100 last:border-b-0"
                  >
                    <td
                      class="px-5 py-4 text-sm text-gray-700"
                    >
                      {{ row.label | translate }}
                    </td>

                    @for (year of years; track year) {
                      <td
                        class="px-5 py-4 text-end text-sm text-gray-700"
                      >
                        {{ (demographicModel[getFieldKey(row.key, year)] | amount) || '-' }}
                      </td>
                    }
                  </tr>
                }
              }

              @if (!hasSelectedRows(section)) {
                <tr>
                  <td
                    [attr.colspan]="
                      years.length + 1
                    "
                    class="px-5 py-6 text-center text-sm text-gray-500"
                  >
                    {{ 'INCOME_STATEMENT.NO_DATA_SELECTED' | translate }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  </div>
}
